import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, Share2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { SelfTestNotice } from './SelfTestNotice';
import { useCompleteV2Assessment } from '../hooks/useV2TakeAssessment';
import { AccountHandoffActions } from './flowScreens';

const GREEN = 'bg-[#026857] hover:bg-[#025247]';

type V2CompletionScreenProps = {
  token: string;
  assessmentId: string;
  advisorName: string;
  clientEmail: string;
  /** The advisor is taking this themselves — see SelfTestNotice. */
  isSelfTest: boolean;
  /** Whether this email already has an account — picks sign-in vs create path (Feature 095 split). */
  hasAccount: boolean;
  /** Starts account creation (route layer wires this to the guest signup modal). */
  onCreateAccount: (email: string) => void;
  /** Starts normal sign-in for an existing account. */
  onSignIn: () => void;
};

/**
 * Sharing choice + account handoff for MVP 2 (Feature 110).
 *
 * The choice is framed as opt-out, not opt-in: V1 asked the client to pick and
 * defaulted to private, and MVP 2 flips that "to try and maximize FP access".
 * So sharing is the primary action and declining is the quieter one beside it —
 * but it is still a real choice on the screen, not a pre-ticked box, and the
 * moment it is made is recorded either way.
 *
 * On the advisor's own test run (Feature 118) this screen is the one place the
 * take flow cannot simply be reused as-is. Both halves of it addressed someone
 * the advisor is not: the choice asked them to share their results with
 * themselves, by name, and the handoff offered to create an account they are
 * already signed into — which opened the guest signup modal. The questions
 * before it needed no such change, because they ask about a household and an
 * advisor has one of those too.
 *
 * The screen is still shown rather than skipped: it is where an advisor finds
 * out whether they will see their client's results, which makes it the most
 * worthwhile screen in the flow for them to meet before a client does.
 */
export function V2CompletionScreen({
  token,
  assessmentId,
  advisorName,
  clientEmail,
  isSelfTest,
  hasAccount,
  onCreateAccount,
  onSignIn,
}: V2CompletionScreenProps) {
  const navigate = useNavigate();
  const complete = useCompleteV2Assessment(token);
  const [declined, setDeclined] = useState(false);

  // A client reads their advisor's name here. An advisor reading their own would
  // be asked to share with themselves, so the preview uses the generic phrasing
  // and the notice above says whose view this is.
  const advisor = isSelfTest ? 'your advisor' : advisorName;

  const choose = (declineSharing: boolean) => {
    setDeclined(declineSharing);
    complete.mutate(declineSharing);
  };

  if (complete.isSuccess) {
    return (
      <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#026857] text-white">
          <Check className="h-6 w-6" />
        </div>
        <h2 className="font-serif text-2xl text-gray-900">
          {isSelfTest ? 'That’s the end of the client’s flow' : 'Your answers are saved'}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
          {isSelfTest ? (
            <>
              A client would create their account here to see their report. Your test run is saved
              and stays out of your stats and client list — and both reports are open to you
              whichever choice you just made.
            </>
          ) : (
            <>
              {declined
                ? `Your results stay private. You can choose to share them with ${advisorName} later.`
                : `${advisorName} will be able to see your results.`}{' '}
              {hasAccount
                ? 'Sign in to view your full report.'
                : 'Create your account to view your full report.'}
            </>
          )}
        </p>
        {isSelfTest ? (
          <Button
            type="button"
            className={`mt-6 ${GREEN}`}
            onClick={() => navigate(`/longevity-assessment/results/${assessmentId}`)}
          >
            View your report
          </Button>
        ) : (
          // Feature 095 split: existing accounts lead with sign-in, new ones
          // with account creation; the handoff stashes the claim token itself.
          <AccountHandoffActions
            token={token}
            clientEmail={clientEmail}
            hasAccount={hasAccount}
            onCreateAccount={onCreateAccount}
            onSignIn={onSignIn}
          />
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
      {isSelfTest && <SelfTestNotice />}
      <h2 className="font-serif text-2xl text-gray-900">You&apos;ve completed the assessment</h2>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
        Your results will be shared with {advisor} so they can help you act on them.
      </p>

      {complete.isError && (
        <p className="mt-3 text-sm text-red-600">Something went wrong. Please try again.</p>
      )}

      <div className="mt-6 space-y-3 text-left">
        <button
          type="button"
          disabled={complete.isPending}
          onClick={() => choose(false)}
          className="flex w-full items-start gap-3 rounded-xl border border-[#026857] bg-[#eaf1ee] p-4 text-left transition-colors disabled:opacity-60"
        >
          <Share2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#026857]" aria-hidden />
          <span>
            <span className="block font-medium text-gray-900">
              Share my results with {advisor}
            </span>
            <span className="block text-sm text-gray-500">
              Your advisor sees your score, all five pillars, and the priorities you were given.
            </span>
          </span>
        </button>

        <button
          type="button"
          disabled={complete.isPending}
          onClick={() => choose(true)}
          className="flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-gray-300 disabled:opacity-60"
        >
          <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden />
          <span>
            <span className="block font-medium text-gray-900">Keep my results private for now</span>
            <span className="block text-sm text-gray-500">
              Only you can see them. You can share later from your dashboard.
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}
